import React, { createContext, useContext, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";

const ReviewsContext = createContext(null);

export function ReviewsProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const getReviewsForExpert = useCallback(async (expertId) => {
    setLoading(true);

    // 1. Попытка загрузки через наш Express / Prisma бэкенд
    try {
      const res = await fetch(`/api/reviews?expertId=${expertId}`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.reviews)) {
          setLoading(false);
          return data.reviews.map((r) => ({
            id: r.id,
            author: r.client?.name || "Клиент",
            rating: r.rating,
            text: r.comment || r.text || "",
            date: r.createdAt || r.created_at,
            expertReply: r.expertReply || null,
            replyDate: r.replyDate || null,
          }));
        }
      }
    } catch (apiErr) {
      console.warn("[getReviewsForExpert] API fetch failed, trying fallback:", apiErr);
    }

    // 2. Fallback через Supabase
    try {
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .eq("expert_id", expertId)
        .order("created_at", { ascending: false });

      setLoading(false);

      if (error) {
        console.warn("Failed to fetch reviews:", error);
        return [];
      }

      return (data || []).map((r) => ({
        id: r.id,
        author: r.author_name,
        rating: r.rating,
        text: r.text,
        date: r.created_at,
        expertReply: r.expert_reply || null,
        replyDate: r.reply_date || null
      }));
    } catch (err) {
      console.warn("Error fetching reviews:", err);
      setLoading(false);
      return [];
    }
  }, []);

  const saveExpertReply = useCallback(async (reviewId, replyText) => {
    if (!user) return false;

    const token = localStorage.getItem("obstuchim_token");
    if (token) {
      try {
        const res = await fetch(`/api/reviews/${reviewId}/reply`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ replyText }),
        });
        if (res.ok) return true;
      } catch (apiErr) {
        console.warn("[saveExpertReply] API reply failed, trying fallback:", apiErr);
      }
    }

    const { error } = await supabase
      .from("reviews")
      .update({
        expert_reply: replyText,
        reply_date: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq("id", reviewId);

    if (error) {
      console.warn("Failed to save reply:", error);
      return false;
    }
    return true;
  }, [user]);

  const submitReview = useCallback(async (expertId, rating, text, consultationId) => {
    if (!user) return null;

    const token = localStorage.getItem("obstuchim_token");
    if (token) {
      try {
        const res = await fetch("/api/reviews", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            expertId,
            rating,
            comment: text,
            consultationId,
          }),
        });
        if (res.ok) {
          const json = await res.json();
          const r = json.review;
          return {
            id: r.id,
            author: r.client?.name || user.name || "Клиент",
            rating: r.rating,
            text: r.comment,
            date: r.createdAt,
            expertReply: null,
            replyDate: null,
          };
        }
      } catch (apiErr) {
        console.warn("[submitReview] API submit failed, trying fallback:", apiErr);
      }
    }

    const { data, error } = await supabase
      .from("reviews")
      .insert({
        expert_id: expertId,
        client_id: user.id,
        author_name: user.name || "Клиент",
        rating,
        text
      })
      .select()
      .single();

    if (error) {
      console.warn("Failed to submit review:", error);
      return null;
    }

    return {
      id: data.id,
      author: data.author_name,
      rating: data.rating,
      text: data.text,
      date: data.created_at,
      expertReply: null,
      replyDate: null
    };
  }, [user]);

  return (
    <ReviewsContext.Provider
      value={{
        loading,
        getReviewsForExpert,
        saveExpertReply,
        submitReview
      }}
    >
      {children}
    </ReviewsContext.Provider>
  );
}

export function useReviews() {
  const context = useContext(ReviewsContext);
  if (!context) {
    throw new Error("useReviews must be used within a ReviewsProvider");
  }
  return context;
}

